import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  IconButton,
  Alert,
  Snackbar,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Inventory as InventoryIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import affiliateService from '../services/affiliateService';
import SingleImageUpload from '../components/SingleImageUpload';

function AffiliateCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openProductsDialog, setOpenProductsDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignProducts, setCampaignProducts] = useState([]);
  const [productFormData, setProductFormData] = useState({
    name: '',
    description: '',
    image: '',
    estimatedValue: '',
    quantityAvailable: 1,
  });
  const [editingProduct, setEditingProduct] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'product',
    content: {},
    image: '',
    isActive: true,
    startDate: '',
    endDate: '',
    priority: 0,
    maxParticipants: null,
  });

  // Fonction pour obtenir l'URL complète de l'image
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // Si c'est déjà une URL complète
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // Sinon, ajouter le base URL
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr';
    return `${baseUrl}${imagePath}`;
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await affiliateService.getCampaigns();
      setCampaigns(data.data || []);
      setFilteredCampaigns(data.data || []);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des campagnes', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCampaigns(campaigns);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = campaigns.filter(campaign => 
        campaign.title?.toLowerCase().includes(term) ||
        campaign.description?.toLowerCase().includes(term) ||
        campaign.type?.toLowerCase().includes(term)
      );
      setFilteredCampaigns(filtered);
    }
  }, [searchTerm, campaigns]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleOpenDialog = (campaign = null) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setFormData({
        title: campaign.title || '',
        description: campaign.description || '',
        type: campaign.type || 'product',
        content: campaign.content || {},
        image: campaign.image || '',
        isActive: campaign.isActive !== undefined ? campaign.isActive : true,
        startDate: campaign.startDate ? campaign.startDate.split('T')[0] : '',
        endDate: campaign.endDate ? campaign.endDate.split('T')[0] : '',
        priority: campaign.priority || 0,
        maxParticipants: campaign.maxParticipants || null,
      });
    } else {
      setEditingCampaign(null);
      setFormData({
        title: '',
        description: '',
        type: 'product',
        content: {},
        image: '',
        isActive: true,
        startDate: '',
        endDate: '',
        priority: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingCampaign) {
        await affiliateService.updateCampaign(editingCampaign.id, formData);
        showSnackbar('Campagne mise à jour');
      } else {
        await affiliateService.createCampaign(formData);
        showSnackbar('Campagne créée');
      }
      setOpenDialog(false);
      loadCampaigns();
    } catch (error) {
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cette campagne ?')) {
      try {
        await affiliateService.deleteCampaign(id);
        showSnackbar('Campagne supprimée');
        loadCampaigns();
      } catch (error) {
        showSnackbar('Erreur lors de la suppression', 'error');
      }
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await affiliateService.toggleCampaignActive(id);
      showSnackbar('Statut mis à jour');
      loadCampaigns();
    } catch (error) {
      showSnackbar('Erreur lors de la mise à jour', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenProductsDialog = async (campaign) => {
    try {
      setSelectedCampaign(campaign);
      setEditingProduct(null);
      setProductFormData({
        name: '',
        description: '',
        image: '',
        estimatedValue: '',
        quantityAvailable: 1,
      });
      
      // Charger les produits dédiés de la campagne
      const campaignData = await affiliateService.getCampaignProducts(campaign.id);
      setCampaignProducts(campaignData.products || []);

      setOpenProductsDialog(true);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des produits', 'error');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductFormData({
      name: product.name || '',
      description: product.description || '',
      image: product.image || '',
      estimatedValue: product.estimatedValue || '',
      quantityAvailable: product.quantityAvailable || 1,
    });
  };

  const handleSaveProduct = async () => {
    try {
      if (editingProduct) {
        // Mettre à jour un produit existant
        await affiliateService.updateCampaignProduct(selectedCampaign.id, editingProduct.id, productFormData);
        showSnackbar('Produit mis à jour avec succès');
      } else {
        // Créer un nouveau produit
        await affiliateService.addCampaignProduct(selectedCampaign.id, productFormData);
        showSnackbar('Produit créé avec succès');
      }

      // Recharger les produits
      const campaignData = await affiliateService.getCampaignProducts(selectedCampaign.id);
      setCampaignProducts(campaignData.products || []);
      
      // Réinitialiser le formulaire
      setEditingProduct(null);
      setProductFormData({
        name: '',
        description: '',
        image: '',
        estimatedValue: '',
        quantityAvailable: 1,
      });
    } catch (error) {
      showSnackbar('Erreur lors de la sauvegarde du produit', 'error');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Supprimer ce produit ?')) {
      try {
        await affiliateService.deleteCampaignProduct(selectedCampaign.id, productId);
        showSnackbar('Produit supprimé');
        
        // Recharger les produits
        const campaignData = await affiliateService.getCampaignProducts(selectedCampaign.id);
        setCampaignProducts(campaignData.products || []);
      } catch (error) {
        showSnackbar('Erreur lors de la suppression', 'error');
      }
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      product: 'Produit',
      offer: 'Offre',
      info: 'Information',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      product: 'primary',
      offer: 'success',
      info: 'info',
    };
    return colors[type] || 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Campagnes d'affiliation</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nouvelle campagne
        </Button>
      </Box>

      {/* Barre de recherche */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher par titre, description, type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: searchTerm && (
              <IconButton size="small" onClick={handleClearSearch}>
                <ClearIcon />
              </IconButton>
            ),
          }}
        />
      </Box>

      <Grid container spacing={3}>
        {filteredCampaigns.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchTerm ? 'Aucun résultat trouvé' : 'Aucune campagne'}
              </Typography>
            </Paper>
          </Grid>
        ) : (
          filteredCampaigns.map((campaign) => (
          <Grid item xs={12} md={6} lg={4} key={campaign.id}>
            <Card sx={{ height: '100%' }}>
              {campaign.image && (
                <Box
                  component="img"
                  src={getImageUrl(campaign.image)}
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                />
              )}
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Chip label={getTypeLabel(campaign.type)} color={getTypeColor(campaign.type)} size="small" />
                  <Chip 
                    label={campaign.isActive ? 'Active' : 'Inactive'} 
                    color={campaign.isActive ? 'success' : 'default'} 
                    size="small" 
                  />
                </Box>

                <Typography variant="h6" gutterBottom>
                  {campaign.title}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {campaign.description}
                </Typography>

                {campaign.startDate && (
                  <Typography variant="caption" display="block">
                    Début : {new Date(campaign.startDate).toLocaleDateString('fr-FR')}
                  </Typography>
                )}
                {campaign.endDate && (
                  <Typography variant="caption" display="block">
                    Fin : {new Date(campaign.endDate).toLocaleDateString('fr-FR')}
                  </Typography>
                )}

                {campaign.maxParticipants && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 'bold' }}>
                    👥 {campaign.currentParticipants || 0} / {campaign.maxParticipants} participants
                  </Typography>
                )}

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  {campaign.type === 'product' && (
                    <IconButton onClick={() => handleOpenProductsDialog(campaign)} size="small" color="secondary">
                      <InventoryIcon />
                    </IconButton>
                  )}
                  <IconButton onClick={() => handleToggleActive(campaign.id)} size="small">
                    <ViewIcon />
                  </IconButton>
                  <IconButton onClick={() => handleOpenDialog(campaign)} size="small" color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(campaign.id)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          ))
        )}
      </Grid>

      {/* Dialog création/édition */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCampaign ? 'Modifier la campagne' : 'Nouvelle campagne'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Titre"
                fullWidth
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <MenuItem value="product">Produit</MenuItem>
                  <MenuItem value="offer">Offre</MenuItem>
                  <MenuItem value="info">Information</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Priorité"
                fullWidth
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Nombre max de participants"
                fullWidth
                type="number"
                value={formData.maxParticipants || ''}
                onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value ? parseInt(e.target.value) : null })}
                helperText="Laissez vide pour illimité"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Date de début"
                fullWidth
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Date de fin"
                fullWidth
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <SingleImageUpload
                currentImage={formData.image}
                onImageChange={(path) => setFormData({ ...formData, image: path })}
                uploadEndpoint="/api/admin/affiliate-campaigns/upload-image"
                title="Image de la campagne"
                maxSize={5}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          <Button onClick={handleSave} variant="contained">
            {editingCampaign ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour gérer les produits d'une campagne */}
      <Dialog open={openProductsDialog} onClose={() => setOpenProductsDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Produits de la campagne - {selectedCampaign?.title}
        </DialogTitle>
        <DialogContent>
          {/* Formulaire d'ajout/édition de produit */}
          <Paper sx={{ p: 3, mb: 3, backgroundColor: 'background.default' }}>
            <Typography variant="h6" gutterBottom>
              {editingProduct ? 'Modifier le produit' : 'Ajouter un produit'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nom du produit"
                  fullWidth
                  required
                  value={productFormData.name}
                  onChange={(e) => setProductFormData({ ...productFormData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Valeur estimée (€)"
                  fullWidth
                  type="number"
                  value={productFormData.estimatedValue}
                  onChange={(e) => setProductFormData({ ...productFormData, estimatedValue: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Quantité disponible"
                  fullWidth
                  type="number"
                  value={productFormData.quantityAvailable}
                  onChange={(e) => setProductFormData({ ...productFormData, quantityAvailable: parseInt(e.target.value) || 1 })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={2}
                  value={productFormData.description}
                  onChange={(e) => setProductFormData({ ...productFormData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <SingleImageUpload
                  currentImage={productFormData.image}
                  onImageChange={(path) => setProductFormData({ ...productFormData, image: path })}
                  uploadEndpoint="/api/admin/affiliate-campaigns/upload-product-image"
                  title="Image du produit"
                  maxSize={5}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="contained" 
                    onClick={handleSaveProduct}
                    disabled={!productFormData.name}
                  >
                    {editingProduct ? 'Mettre à jour' : 'Ajouter'}
                  </Button>
                  {editingProduct && (
                    <Button 
                      variant="outlined" 
                      onClick={() => {
                        setEditingProduct(null);
                        setProductFormData({
                          name: '',
                          description: '',
                          image: '',
                          estimatedValue: '',
                          quantityAvailable: 1,
                        });
                      }}
                    >
                      Annuler
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Liste des produits */}
          <Typography variant="h6" gutterBottom>
            Produits de la campagne ({campaignProducts.length})
          </Typography>
          
          <Box sx={{ maxHeight: '50vh', overflow: 'auto' }}>
            {campaignProducts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Aucun produit dans cette campagne
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {campaignProducts.map((product) => {
                  const baseUrl = process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr';
                  const imageUrl = product.image 
                    ? (product.image.startsWith('http') ? product.image : `${baseUrl}${product.image}`)
                    : null;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={product.id}>
                      <Card>
                        {/* Image du produit */}
                        <Box
                          sx={{
                            width: '100%',
                            height: 140,
                            backgroundColor: 'grey.200',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {imageUrl ? (
                            <Box
                              component="img"
                              src={imageUrl}
                              alt={product.name}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <Typography variant="h3" color="text.disabled">
                              📦
                            </Typography>
                          )}
                        </Box>

                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            {product.name}
                          </Typography>
                          {product.estimatedValue && (
                            <Typography variant="h6" color="primary" gutterBottom>
                              {product.estimatedValue}€
                            </Typography>
                          )}
                          {product.description && (
                            <Typography variant="caption" color="text.secondary">
                              {product.description}
                            </Typography>
                          )}
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            Quantité: {product.quantityAvailable}
                          </Typography>
                        </CardContent>
                        
                        <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            fullWidth
                            onClick={() => handleEditProduct(product)}
                          >
                            Modifier
                          </Button>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="error"
                            fullWidth
                            onClick={() => handleDeleteProduct(product.id)}
                          >
                            Supprimer
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProductsDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AffiliateCampaigns;

